import { Vehicle } from '@otw/models/inventory'
import { formatDateTime, formatDistance, formatPhoneNumber, formatPrice } from 'core/utils/inventoryUtils'
import type { RecordField } from 'components/DenseRecordSheet'
import type { VehicleDealMetrics } from 'hooks/graybook/calculateDealForVehicle'
import { getDealQualityInfo } from 'hooks/graybook/useCalculateDeal'

const yesNo = (value: boolean | null | undefined) => {
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  return '—'
}

const text = (value: unknown) => {
  if (value == null || value === '') return '—'
  return String(value)
}

const money = (value: unknown) => {
  if (value == null || value === '') return '—'
  return formatPrice(value as string | number)
}

const formatPercent = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) return '—'
  return `${value.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`
}

const formatPickup = (vehicle: Vehicle) => {
  const location = vehicle.pickup_location
  if (!location) return '—'
  const parts = [location.address, location.city, location.state, location.zip].filter(Boolean)
  return parts.length ? parts.join(', ') : '—'
}

const formatReadyForTransport = (vehicle: Vehicle) => {
  const ready = vehicle.ready_for_transport
  if (!ready) return '—'
  const flags = [
    ready.ready_for_pickup ? 'ready for pickup' : null,
    ready.needs_address ? 'needs address' : null,
    ready.customer ? 'customer' : null,
    ready.repair ? 'repair' : null,
    ready.note,
  ].filter(Boolean)
  return flags.length ? flags.join(', ') : '—'
}

export const buildVehicleRecordFields = ({
  vehicle,
  deal,
  canViewPurchasePrice,
  canViewProfit,
  canViewProfitRealized,
}: {
  vehicle: Vehicle
  deal: VehicleDealMetrics | null
  canViewPurchasePrice: boolean
  canViewProfit: boolean
  canViewProfitRealized: boolean
}): RecordField[] => {
  const storedDeal = vehicle.dealQuality
  const dealQuality = deal?.dealQuality ?? storedDeal
  const dealInfo = dealQuality ? getDealQualityInfo(dealQuality) : null

  return [
    { label: 'Stock Number', value: text(vehicle.stock_number) },
    { label: 'VIN', value: text(vehicle.vin) },
    { label: 'Status', value: text(vehicle.status) },
    { label: 'Year', value: text(vehicle.year) },
    { label: 'Make', value: text(vehicle.make) },
    { label: 'Model', value: text(vehicle.model) },
    { label: 'Type', value: text(vehicle.type) },
    { label: 'Size', value: text(vehicle.size) },
    { label: 'Size Confirmed', value: vehicle.size_confirmed === true ? 'yes' : vehicle.size_confirmed === false ? 'no' : 'unknown' },
    { label: 'Condition', value: text(vehicle.condition) },
    { label: 'Color', value: text(vehicle.color) },
    { label: 'Fuel', value: text(vehicle.fuel) },
    { label: 'Miles', value: vehicle.miles != null ? `${formatDistance(vehicle.miles)} mi` : '—' },
    { label: 'Rear Wheel', value: text(vehicle.rear_wheel) },
    { label: 'Location', value: text(vehicle.location) },
    { label: 'Pickup Address', value: formatPickup(vehicle) },
    { label: 'Who', value: text(vehicle.who) },
    { label: 'Phone Number', value: vehicle.phone_number ? formatPhoneNumber(vehicle.phone_number) : '—' },
    { label: 'Email', value: text(vehicle.email) },
    { label: 'Deal Quality', value: dealInfo?.label ?? '—' },
    { label: 'Seller Asking Price', value: money(vehicle.seller_asking_price), hidden: !canViewPurchasePrice },
    { label: 'List Price', value: money(vehicle.optic_list_price) },
    { label: 'Gray Book', value: deal?.grayBookRetailPrice != null ? money(deal.grayBookRetailPrice) : '—' },
    { label: 'LTV %', value: formatPercent(deal?.ltv) },
    { label: 'Profit', value: money(vehicle.profit), hidden: !canViewProfit },
    { label: 'Profit Realized', value: yesNo(vehicle.profit_realized), hidden: !canViewProfitRealized },
    { label: 'Paid For', value: yesNo(vehicle.paid_for) },
    { label: 'Funded', value: yesNo(vehicle.funded) },
    { label: 'Show on Website', value: yesNo(vehicle.show_on_website) },
    { label: 'Mechanical', value: text(vehicle.mechanical_status) },
    { label: 'Body', value: text(vehicle.body_status) },
    { label: 'Safety', value: money(vehicle.estimated_safety) },
    { label: 'Ready for Transport', value: formatReadyForTransport(vehicle) },
    { label: 'Delivered', value: yesNo(vehicle.delivered_to_customer) },
    { label: 'Refi', value: yesNo(vehicle.refinance) },
    { label: 'Ready for Sale', value: yesNo(vehicle.ready_for_sale) },
    { label: 'Bought From', value: text(vehicle.bought_from) },
    { label: 'Sold To', value: text(vehicle.sold_to_user?.name) },
    { label: 'Sold By', value: text(vehicle.sold_by_user?.name) },
    { label: 'Selling To', value: text(vehicle.selling_to_user?.name) },
    { label: 'Payment Type', value: text(vehicle.finance?.payment_type) },
    { label: 'Bank Name', value: text(vehicle.finance?.bank_name) },
    { label: 'Payoff Amount', value: money(vehicle.lien_payoff_amount) },
    { label: 'Repair Cost', value: money(vehicle.repair_cost) },
    { label: 'Estimated Repair Cost', value: money(vehicle.estimated_repair_cost) },
    { label: 'Shipping Cost', value: money(vehicle.shipping_cost) },
    { label: 'Date Added', value: vehicle.date_added ? formatDateTime(vehicle.date_added) : '—' },
    { label: 'Sold Date', value: vehicle.sold_date ? formatDateTime(vehicle.sold_date) : '—' },
    { label: 'Available Date', value: vehicle.date_available ? formatDateTime(vehicle.date_available) : '—' },
    { label: 'Cameras', value: text(vehicle.cameras) },
    { label: 'Shelves', value: text(vehicle.shelves) },
    { label: 'GVWR over 10K', value: text(vehicle.gwvr_over_10k) },
    { label: 'Favorite', value: yesNo(vehicle.favorite) },
    { label: 'Truck Notes', value: text(vehicle.truck_notes) },
    { label: 'Follow Up Notes', value: text(vehicle.follow_up_notes) },
    { label: 'Entered By', value: text(vehicle.entered_by) },
    { label: 'Link', value: text(vehicle.link) },
    { label: 'ID', value: text(vehicle.id) },
  ]
}
